import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, CircleDashed } from "lucide-react";

import {
  platformZaloApi,
  platformZaloQueryKeys,
  type PlatformZaloRow,
} from "../../shared/api/platformZalo";
import { apiError } from "../../shared/api/client";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

/**
 * Cấu hình Zalo cho từng phòng khám — một dòng mỗi phòng khám, quản trị viên khai một lần.
 *
 * <p>Trang này tồn tại để KHÁCH HÀNG KHÔNG PHẢI LÀM GÌ. Trước đây phòng khám phải tự vào HIS
 * /miniapp nhập Mini App ID, và bộ credential Zalo thì chỉ có một bộ dùng chung — nên phòng khám
 * nào có Zalo App riêng là phải chờ quản trị viên khai hộ từng lần. Cả hai đều sai với thực tế:
 * khách hàng không có tài khoản developers.zalo.me và không nên phải đi lấy secret ở đâu cả.
 *
 * <p>Khai xong ở đây thì mọi thứ chảy sang Addy Sites qua sync, và bên đó khách hàng chỉ còn sửa
 * nội dung rồi bấm Phát hành.
 *
 * <p>Không hiện giá trị bí mật, kể cả cho quản trị viên — chỉ "đã có" và bốn ký tự cuối. Ô BÍ
 * MẬT để trống nghĩa là giữ nguyên, vì không ai gõ lại được thứ họ không nhìn thấy.
 *
 * <p><b>Mini App ID thì ngược lại: để trống nghĩa là GỠ liên kết.</b> Nó là ô duy nhất ở đây hiện
 * nguyên giá trị đang lưu, nên xoá trắng rồi bấm Lưu chỉ có thể đọc là "bỏ mã này" — và trước
 * đây nó bị đọc thành "giữ nguyên": nút báo lưu xong, giá trị cũ quay lại, không ai hiểu vì sao
 * (#19431). Gỡ là việc mất mát nên màn hình hỏi lại trước.
 */
export function PlatformZaloSetupPage() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: platformZaloQueryKeys.all(),
    queryFn: () => platformZaloApi.list(),
  });

  const [openId, setOpenId] = useState<string | null>(null);

  if (q.isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-7 w-64" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  const rows = q.data ?? [];
  const missing = rows.filter((r) => !r.buildable);

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-lg font-bold text-foreground">Cấu hình Zalo theo phòng khám</h1>
        <p className="mt-1 max-w-prose text-sm text-muted-foreground">
          Mỗi phòng khám một dòng: Mini App và tài khoản Zalo của chính nó. Khai một lần ở đây thì bên Addy
          Sites khách hàng chỉ còn <b>sửa nội dung và bấm Phát hành</b> — họ không phải lấy gì từ
          Zalo, không chạy lệnh, không dán token.
        </p>
      </div>

      {q.isError && (
        <p className="text-sm text-destructive">{apiError(q.error).detail}</p>
      )}

      {/* Ba bước, đặt ngay đầu trang. Không phải trang trí: người mở trang này lần đầu không biết
          giá trị nào lấy ở đâu, và cũng không biết phần nào là việc của KHÁCH HÀNG (không phần
          nào cả) — thiếu câu đó thì họ đi hỏi, hoặc tệ hơn, bảo khách hàng tự đi lấy. */}
      <div className="rounded-lg border bg-muted/30 p-4">
        <h2 className="text-sm font-semibold text-foreground">Cách dùng trang này</h2>
        <ol className="mt-2 max-w-3xl list-decimal space-y-1.5 pl-5 text-sm text-muted-foreground">
          <li>
            <b className="text-foreground">Mỗi phòng khám một dòng:</b> bấm <b>Khai</b> ở bảng
            dưới, điền <b>Mini App ID</b> (lấy ở mini.zalo.me).
          </li>
          <li>
            <b className="text-foreground">Tài khoản Zalo của chính Mini App đó:</b> App ID +
            Secret key lấy ở <b>developers.zalo.me → Ứng dụng</b>, Refresh token ở{" "}
            <b>Công cụ → API Explorer</b>. Mỗi Mini App một bộ riêng — một access token không dùng
            được cho Mini App khác.
          </li>
          <li>
            <b className="text-foreground">Xong.</b> Cột <b>Phát hành được</b> chuyển sang{" "}
            <b>Được</b>. Từ đó khách hàng chỉ vào Addy Sites sửa nội dung và bấm Phát hành — họ
            không phải lấy gì từ Zalo.
          </li>
        </ol>
        <p className="mt-2 max-w-3xl text-xs text-muted-foreground">
          Zalo <b>xoay vòng</b> refresh token sau mỗi lần phát hành và hệ thống tự ghi lại, nên chỉ
          nhập một lần. Nếu khoảng 3 tháng không phát hành lần nào thì Zalo huỷ chuỗi và phải lấy
          lại ở API Explorer.
        </p>
      </div>

      {missing.length > 0 && (
        <div className="rounded-md border border-amber-600/30 bg-amber-600/5 p-3 text-sm">
          <b>{missing.length} phòng khám chưa phát hành được.</b>{" "}
          {missing.map((r) => r.clinicName).join(", ")} — thiếu Mini App ID hoặc thiếu tài khoản
          Zalo.
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border">
        <table className="w-full min-w-[52rem] text-sm">
          <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-3 py-2 text-left font-medium">Phòng khám</th>
              <th className="px-3 py-2 text-left font-medium">Mini App ID</th>
              <th className="px-3 py-2 text-left font-medium">Ứng dụng Zalo</th>
              <th className="px-3 py-2 text-left font-medium">Refresh token</th>
              <th className="px-3 py-2 text-left font-medium">Phát hành được</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-3 py-6 text-center text-muted-foreground">
                  Chưa có phòng khám nào.
                </td>
              </tr>
            ) : (
              rows.map((r) => (
                <SetupRow
                  key={r.tenantId}
                  row={r}
                  open={openId === r.tenantId}
                  onToggle={() => setOpenId(openId === r.tenantId ? null : r.tenantId)}
                  onSaved={() => {
                    setOpenId(null);
                    void qc.invalidateQueries({ queryKey: platformZaloQueryKeys.all() });
                  }}
                  // Kéo xong thì bảng phải đổi ngay, nhưng KHÔNG đóng dòng đang mở: người dùng
                  // vừa bấm để xem App ID xuất hiện, đóng lại là giấu mất kết quả họ vừa xin.
                  onSynced={() => {
                    void qc.invalidateQueries({ queryKey: platformZaloQueryKeys.all() });
                  }}
                />
              ))
            )}
          </tbody>
        </table>
      </div>

    </div>
  );
}

function SetupRow({
  row,
  open,
  onToggle,
  onSaved,
  onSynced,
}: {
  row: PlatformZaloRow;
  open: boolean;
  onToggle: () => void;
  onSaved: () => void;
  onSynced: () => void;
}) {
  const [zaloAppId, setZaloAppId] = useState("");
  const [refreshToken, setRefreshToken] = useState("");
  const [paymentPrivateKey, setPaymentPrivateKey] = useState("");
  const [error, setError] = useState<string | null>(null);
  /** Hộp xác nhận gỡ liên kết — xem chỗ bấm Lưu bên dưới. */
  const [confirmUnbind, setConfirmUnbind] = useState(false);

  // Nạp giá trị đang lưu làm điểm xuất phát, trừ ô bí mật — nó luôn trống vì API không trả giá
  // trị, và trống nghĩa là "giữ nguyên". App ID/Secret không còn ở đây: Addy Message sở hữu chúng,
  // HIS chỉ hiển thị bản sao.
  //
  // CHỈ NẠP MỘT LẦN mỗi lần mở dòng. Trước đây effect phụ thuộc cả `row`, mà bấm "Lấy App ID" xong
  // thì danh sách được nạp lại và `row` thành một object mới — effect chạy lại và ghi đè đúng thứ
  // người dùng vừa gõ: Mini App ID biến mất, refresh token vừa dán bị xoá, công tắc bật lên lại
  // theo giá trị máy chủ. Người dùng bấm một nút để LẤY THÊM thông tin, không đời nào ngờ nó xoá
  // những gì mình đang nhập dở.
  //
  // Hai cột App ID và Secret vẫn cập nhật ngay sau khi đồng bộ, vì chúng đọc thẳng từ `row` chứ
  // không đi qua state của form.
  const seededFor = useRef<string | null>(null);
  useEffect(() => {
    if (!open) {
      seededFor.current = null;
      return;
    }
    if (seededFor.current === row.tenantId) return;
    seededFor.current = row.tenantId;
    setZaloAppId(row.zaloAppId ?? "");
    setRefreshToken("");
    setPaymentPrivateKey("");
    setError(null);
  }, [open, row]);

  const save = useMutation({
    mutationFn: () =>
      platformZaloApi.save(row.tenantId, {
        zaloAppId: zaloAppId.trim(),
        // App identity is not sent: Addy Message authors it and the API refuses it here.
        refreshToken: refreshToken.trim(),
        zaloPaymentPrivateKey: paymentPrivateKey.trim(),
      }),
    onSuccess: onSaved,
    onError: (e) => setError(apiError(e).detail),
  });

  return (
    <>
      <tr className={cn("border-t", open && "bg-muted/30")}>
        <td className="px-3 py-2">
          <div className="font-medium text-foreground">{row.clinicName}</div>
          <div className="text-xs text-muted-foreground">{row.tenantCode}</div>
        </td>
        <td className="px-3 py-2">
          {row.zaloAppId ? (
            <code className="text-xs">{row.zaloAppId}</code>
          ) : (
            <span className="text-muted-foreground">chưa có</span>
          )}
          {/*
            Cảnh báo khi phòng khám chưa có App Secret — thứ DUY NHẤT ký được cho đăng nhập
            bệnh nhân. Addy Message khai nó; chưa khai thì bệnh nhân mở app ra không vào được.
          */}
          {row.zaloAppId && !row.loginReady && (
            <div className="text-xs text-destructive">
              chưa có App Secret — bệnh nhân chưa đăng nhập được
            </div>
          )}
        </td>
        <td className="px-3 py-2">
          {row.oauthAppId ? (
            <code className="text-xs">{row.oauthAppId}</code>
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
          {row.oauthAppId && !row.hasAppSecret && (
            <div className="text-xs text-destructive">thiếu secret</div>
          )}
        </td>
        <td className="px-3 py-2">
          {row.hasRefreshToken ? (
            <span className="font-mono text-xs">{row.refreshTokenHint}</span>
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </td>
        <td className="px-3 py-2">
          {row.buildable ? (
            <span className="flex items-center gap-1 text-emerald-600">
              <CheckCircle2 className="h-4 w-4" /> Được
            </span>
          ) : (
            <span className="flex items-center gap-1 text-muted-foreground">
              <CircleDashed className="h-4 w-4" /> Chưa
            </span>
          )}
        </td>
        <td className="px-3 py-2 text-right">
          <Button type="button" variant="outline" size="sm" onClick={onToggle}>
            {open ? "Đóng" : row.buildable ? "Sửa" : "Khai"}
          </Button>
        </td>
      </tr>

      {open && (
        <tr className="border-t bg-muted/20">
          <td colSpan={6} className="px-3 py-4">
            <div className="grid gap-3 md:grid-cols-3">
              <Field
                label="Mini App ID"
                hint={
                  row.zaloAppId
                    ? "mini.zalo.me, ngay dưới tên Mini App — xoá trắng rồi Lưu là gỡ liên kết"
                    : "mini.zalo.me, ngay dưới tên Mini App"
                }
                value={zaloAppId}
                onChange={setZaloAppId}
                placeholder=""
              />
              {/*
                KHÔNG có ô Secret Key ở đây, và đó là điều đúng.

                Zalo cấp MỘT Secret Key cho mỗi Zalo App; các Mini App là app con bên trong nó và
                không có khoá riêng. Khoá dùng để xác thực người dùng Zalo vì thế LUÔN là App
                Secret của Zalo App — thứ Addy Message khai và đẩy sang, hiện ở hai ô chỉ đọc ngay
                dưới. Không có giá trị thứ hai để mà nhập.

                Bản trước có một ô "Secret Key của Mini App", dựng trên hiểu sai rằng mỗi Mini App
                có khoá riêng, và chỉ người đọc đi tìm "mini.zalo.me → Cài đặt → Secret Key" —
                một mục không tồn tại. Một ô đòi thứ không tồn tại thì tệ hơn là không có ô: nó
                làm người khai tin là mình còn thiếu một bước.
              */}
              {/*
                App ID và Secret key giờ do Addy Message quản lý (Phase 3, Option C): nó là sản
                phẩm duy nhất bắt buộc chạy độc lập và là nơi duy nhất thực sự chạy OAuth với
                ứng dụng đó. Hai ô này để CHỈ ĐỌC thay vì bỏ đi — quản trị viên vẫn cần nhìn
                thấy phòng khám đang dùng app nào, và một ô biến mất không nói được là nó đã
                chuyển đi đâu.

                Ô trống nghĩa là HIS CHƯA LẤY VỀ, không phải Message chưa có: hai ô hiện bản sao
                của HIS, chỉ được điền khi bấm nút bên dưới. Câu cũ "chưa khai ở Addy Message"
                khiến người đã khai đủ bên Message tưởng mình còn thiếu và đi khai lại.
              */}
              <Field
                label="Ứng dụng Zalo — App ID"
                hint="do Addy Message quản lý — chỉ đọc"
                value={row.oauthAppId ?? ""}
                onChange={() => {}}
                placeholder="chưa lấy về — bấm “Lấy App ID từ Addy Message”"
                readOnly
              />
              <Field
                label="Secret key"
                hint="do Addy Message quản lý — chỉ đọc"
                value={row.hasAppSecret ? "••••••••" : ""}
                onChange={() => {}}
                placeholder="chưa lấy về — bấm “Lấy App ID từ Addy Message”"
                readOnly
              />
              <Field
                label="Refresh token"
                hint={
                  row.hasRefreshToken
                    ? "đã có — để trống là giữ nguyên"
                    : "Công cụ → API Explorer, bấm nút COPY"
                }
                value={refreshToken}
                onChange={setRefreshToken}
                placeholder={row.hasRefreshToken ? "••••••••" : "dán refresh token"}
              />
              {/*
                Private Key của Zalo Mini App Payment — ô này CÓ THẬT trong console (mở Mini App
                → Payment, cạnh Security Method và Callback Url), khác với ô "Secret Key của
                Mini App" đã gỡ vì thứ đó không tồn tại.

                Khoá ở lại HIS và chỉ dùng để ký chữ ký đơn hàng TRÊN MÁY CHỦ. Không bao giờ gửi
                xuống app: repo addy-mini-app ký ngay trong trình duyệt bằng
                VITE_PAYMENT_PRIVATE_KEY, tức khoá nằm trong gói JS đã phát hành — ai tải app về
                cũng ký được một đơn hàng với số tiền tuỳ ý.
              */}
              <Field
                label="Private Key thanh toán"
                hint={
                  row.hasPaymentPrivateKey
                    ? "đã có — để trống là giữ nguyên"
                    : "console Mini App → Payment → Private Key"
                }
                value={paymentPrivateKey}
                onChange={setPaymentPrivateKey}
                placeholder={row.hasPaymentPrivateKey ? "••••••••" : "dán Private Key"}
              />
            </div>

            {error && <p className="mt-3 text-sm text-destructive">{error}</p>}

            <div className="mt-4 flex flex-wrap items-center gap-2">
              <Button
                type="button"
                disabled={save.isPending}
                onClick={() => {
                  /*
                   * Xoá trắng Mini App ID = gỡ liên kết, và nó XOÁ cả hai khoá nằm trên dòng
                   * đó. Hỏi lại vì hậu quả không đọc được từ màn hình: bệnh nhân mất đường
                   * đăng nhập, và Secret Key lẫn Private Key thanh toán phải khai lại từ đầu.
                   */
                  if (row.zaloAppId && !zaloAppId.trim()) {
                    setConfirmUnbind(true);
                    return;
                  }
                  setError(null);
                  save.mutate();
                }}
              >
                {save.isPending ? "Đang lưu…" : "Lưu"}
              </Button>
              <Button type="button" variant="ghost" onClick={onToggle}>
                Huỷ
              </Button>
              <SyncAppIdentity tenantId={row.tenantId} onSynced={onSynced} />
            </div>

            {/*
              Hộp xác nhận gỡ liên kết — cùng khuôn với mọi hộp khác trong HIS
              (`CancelInvoiceButton`), không phải `window.confirm`.

              Không chỉ vì đẹp: hộp của trình duyệt không nhận được định dạng nào, nên hai hậu
              quả — mất đường đăng nhập, và mất hai khoá — nằm chung một khối chữ xám và đọc
              lướt là trôi mất. Ở đây chúng là hai dòng có dấu đầu dòng, tên phòng khám in đậm,
              và nút thực hiện mang màu cảnh báo thay vì một chữ "OK" trung tính.
            */}
            <Dialog open={confirmUnbind} onOpenChange={setConfirmUnbind}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Gỡ liên kết Mini App?</DialogTitle>
                </DialogHeader>
                <div className="space-y-3 text-sm">
                  <p>
                    Phòng khám <b>{row.clinicName}</b> sẽ không còn Mini App nào được liên kết.
                  </p>
                  <ul className="list-disc space-y-1 pl-5 text-muted-foreground">
                    <li>Bệnh nhân mở app sẽ không đăng nhập được nữa.</li>
                    <li>
                      <b>Secret Key</b> và <b>Private Key thanh toán</b> của phòng khám này bị
                      xoá theo — liên kết lại thì phải khai lại cả hai.
                    </li>
                  </ul>
                  <p className="text-muted-foreground">
                    Mã Mini App vẫn còn trên Zalo; đây chỉ là bỏ liên kết trong Addy HIS.
                  </p>
                </div>
                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setConfirmUnbind(false)}
                  >
                    Giữ nguyên
                  </Button>
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    onClick={() => {
                      // Đóng hộp rồi mới gọi: tiến độ và lỗi đều hiện ở hàng bên dưới (nút Lưu
                      // đổi thành "Đang lưu…", lỗi in ngay trên nó). Giữ hộp mở để hiện trạng
                      // thái thì lớp phủ lại che mất đúng chỗ in lỗi.
                      setConfirmUnbind(false);
                      setError(null);
                      save.mutate();
                    }}
                  >
                    Gỡ liên kết
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </td>
        </tr>
      )}
    </>
  );
}

/**
 * Kéo App ID + Secret từ Addy Message về HIS, theo yêu cầu tường minh.
 *
 * <p>Phase 3 chuyển quyền sở hữu hai giá trị này sang Addy Message nhưng chưa có đường nào để
 * lấy chúng về, nên hai ô chỉ-đọc ở trên đứng trống vĩnh viễn và không nói được vì sao. Nút này
 * là đường đó.
 *
 * <p>Không chạy nền, không chạy khi mở trang. Một job ngầm giữ hai kho khớp nhau chính là thứ
 * mô hình một-người-ghi tránh: nó hỏng lặng lẽ, và bản sao lệch lặng lẽ là lý do có Phase 3.
 * Kết quả nói rõ "không đổi" khác "đã cập nhật" — bằng nhau cũng là một câu trả lời có ích.
 */
function SyncAppIdentity({ tenantId, onSynced }: { tenantId: string; onSynced: () => void }) {
  const [note, setNote] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  const sync = useMutation({
    mutationFn: () => platformZaloApi.syncAppIdentity(tenantId),
    onSuccess: (r) => {
      setFailed(false);
      setNote(
        !r.configured
          ? "Addy Message chưa khai App ID cho phòng khám này."
          : r.changed
            ? `Đã cập nhật App ID ${r.appId ?? ""}${r.hasSecret ? " + secret" : " (chưa có secret)"}.`
            : "Đã khớp sẵn, không có gì thay đổi.",
      );
      onSynced();
    },
    onError: (e) => {
      setFailed(true);
      setNote(apiError(e).detail);
    },
  });

  return (
    <span className="flex flex-wrap items-center gap-2">
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={sync.isPending}
        onClick={() => {
          setNote(null);
          sync.mutate();
        }}
      >
        {sync.isPending ? "Đang lấy…" : "Lấy App ID từ Addy Message"}
      </Button>
      {note && (
        <span className={cn("text-xs", failed ? "text-destructive" : "text-muted-foreground")}>
          {note}
        </span>
      )}
    </span>
  );
}

function Field({
  label,
  hint,
  value,
  onChange,
  placeholder,
  readOnly,
}: {
  label: string;
  hint: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  readOnly?: boolean;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium text-foreground">{label}</span>
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        spellCheck={false}
        readOnly={readOnly}
        className={readOnly ? "bg-muted text-muted-foreground" : undefined}
      />
      <span className="text-xs text-muted-foreground">{hint}</span>
    </label>
  );
}
